import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LifeBuoy, Loader2, ChevronRight, Clock, AlertTriangle, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import {
  CASE_STATUS_META, CASE_TYPE_META, RETURN_STATUS_LABELS, REFUND_STATUS_LABELS,
  caseApi, formatDate, formatMoney, isOpenCase, relativeDeadline,
} from '../lib/orderCases';

const CaseCard = ({ item, role, index, onOpen }) => {
  const meta = CASE_TYPE_META[item.type] || CASE_TYPE_META.OTHER;
  const status = CASE_STATUS_META[item.status] || {};
  const needsMe =
    (role === 'seller' && ['WAITING_FOR_SELLER', 'OPEN', 'SELLER_RESPONSE_OVERDUE'].includes(item.status)) ||
    (role === 'buyer' && ['WAITING_FOR_BUYER', 'WAITING_FOR_ACCEPTANCE'].includes(item.status));
  const deadline = role === 'seller' ? item.seller_deadline : item.buyer_deadline;

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onClick={() => onOpen(item.id)}
      className="w-full text-left"
      data-testid={`case-card-${item.id}`}
    >
      <Card
        className={`p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 border ${
          needsMe ? 'border-amber-200 bg-gradient-to-r from-amber-50/60 to-white' : 'border-slate-200'
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-2xl ${meta.bg} ring-1 ${meta.ring} flex items-center justify-center flex-shrink-0`}>
            <meta.Icon className={`w-5 h-5 ${meta.accent}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-800">{meta.label}</span>
              <Badge className={`${status.cls} border text-xs`}>{status.label || item.status}</Badge>
              {item.priority === 'URGENT' && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Urgente</Badge>
              )}
            </div>

            <p className="text-xs font-mono text-slate-400 mt-1">
              {item.case_number} · Ordine {item.order_number || item.order_id?.slice(0, 8)}
            </p>

            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{item.description}</p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
              <span>{role === 'seller' ? item.buyer_name : item.seller_name}</span>
              <span>{formatMoney(item.order_amount)}</span>
              {item.return_status !== 'NOT_APPLICABLE' && (
                <span className="text-teal-700">{RETURN_STATUS_LABELS[item.return_status]}</span>
              )}
              {item.refund_status !== 'NONE' && (
                <span className="text-amber-700">{REFUND_STATUS_LABELS[item.refund_status]}</span>
              )}
              <span>Aggiornato {formatDate(item.updated_at, true)}</span>
            </div>

            {needsMe && deadline && (
              <div className="flex items-center gap-1.5 mt-3 text-xs font-medium text-amber-700">
                {new Date(deadline) < new Date() ? (
                  <><AlertTriangle className="w-3.5 h-3.5" /> Termine superato</>
                ) : (
                  <><Clock className="w-3.5 h-3.5" /> Rispondi {relativeDeadline(deadline)}</>
                )}
              </div>
            )}
          </div>

          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
        </div>
      </Card>
    </motion.button>
  );
};

/**
 * Sezione "Assistenza ordine" per la dashboard acquirente e artigiano.
 */
export default function AssistanceCasesList({ role = 'buyer' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    (async () => {
      try {
        const data = await caseApi.list(user.id, role);
        if (active) setCases(data || []);
      } catch (e) {
        if (active) setCases([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id, role]);

  const open = cases.filter((c) => isOpenCase(c.status));
  const attention = open.filter((c) =>
    role === 'seller'
      ? ['WAITING_FOR_SELLER', 'OPEN', 'SELLER_RESPONSE_OVERDUE'].includes(c.status)
      : ['WAITING_FOR_BUYER', 'WAITING_FOR_ACCEPTANCE'].includes(c.status)
  );
  const others = open.filter((c) => !attention.includes(c));
  const closed = cases.filter((c) => !isOpenCase(c.status));

  const goTo = (id) => navigate(`/assistenza-ordine/${id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="assistance-cases-section">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#008080] to-[#00CED1] flex items-center justify-center shadow-lg shadow-[#008080]/20">
            <LifeBuoy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Assistenza ordine</h1>
            <p className="text-sm text-slate-500">
              {role === 'seller'
                ? 'Le richieste dei tuoi clienti e le soluzioni proposte'
                : 'Tutte le tue richieste di assistenza, aperte e passate'}
            </p>
          </div>
        </div>
      </div>

      {cases.length === 0 && (
        <Card className="p-12 text-center border-2 border-dashed border-slate-200">
          <div className="w-16 h-16 rounded-2xl bg-[#008080]/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-[#008080]" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Nessuna richiesta di assistenza</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {role === 'seller'
              ? 'Ottimo segno: nessun cliente ha bisogno di aiuto in questo momento.'
              : 'Se qualcosa non va con un ordine, apri una richiesta dal dettaglio dell\'ordine.'}
          </p>
        </Card>
      )}

      {attention.length > 0 && (
        <section>
          <h2 className="text-base md:text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            {role === 'seller' ? 'Richiedono la tua attenzione' : 'In attesa di una tua risposta'}
          </h2>
          <div className="space-y-3">
            {attention.map((c, i) => (
              <CaseCard key={c.id} item={c} role={role} index={i} onOpen={goTo} />
            ))}
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section>
          <h2 className="text-base md:text-lg font-semibold text-slate-700 mb-3">Aperti</h2>
          <div className="space-y-3">
            {others.map((c, i) => (
              <CaseCard key={c.id} item={c} role={role} index={i} onOpen={goTo} />
            ))}
          </div>
        </section>
      )}

      {closed.length > 0 && (
        <section>
          <h2 className="text-base md:text-lg font-semibold text-slate-700 mb-3">Risolti e chiusi</h2>
          <div className="space-y-3">
            {closed.map((c, i) => (
              <CaseCard key={c.id} item={c} role={role} index={i} onOpen={goTo} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
